import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import ReactFlow, {
  Controls,
  Background,
  MarkerType,
  Panel,
  useNodesInitialized,
  useReactFlow
} from 'reactflow';
import { createPortal } from 'react-dom';
import Modal from 'react-bootstrap/Modal';

// Import custom components
import { useGraphService } from './GraphContext';
import UniversalNode from './nodes/NodeTypes';
import { SkeletonNode } from './nodes/SkeletonNode';
import { CustomEdge } from './edges/CustomEdges';
import { TreeEdge } from './edges/TreeEdge';
import NodePanel from './panels/NodePanel';
import EdgePanel from './panels/EdgePanel';
import TransformPanel from './panels/TransformPanel';
import GraphSearch from './GraphSearch';
import CustomAttributesPanel from './panels/CustomAttributesPanel';
import Style from './GraphMapper.module.css';
import { EDGE_COLOR } from './edgeColors';

const nodeTypes = {
  universal: UniversalNode,
  skeleton: SkeletonNode
};

const edgeTypes = {
  custom: CustomEdge,
  tree: TreeEdge
};

const GraphMapper = ({ toolbarSlot, canEdit }) => {
  // Available transformation types
  const transformationTypes = useMemo(() => [
    { value: 'none', label: 'None' },
    { value: 'formatCurrency', label: 'Format Currency' },
    { value: 'formatDate', label: 'Format Date' },
    { value: 'formatNumber', label: 'Format Number' },
    { value: 'uppercase', label: 'UPPERCASE' },
    { value: 'lowercase', label: 'lowercase' },
    { value: 'titleCase', label: 'Title Case' },
    { value: 'trim', label: 'Trim Whitespace' },
    { value: 'custom', label: 'Custom...' }
  ], []);

  // Use the graph service
  const {
    nodes, 
    edges, 
    onNodesChange, 
    onEdgesChange, 
    handleConnect,
    updateEdgeLabel,
    setEdgeTransformation,
    selectNode,
    removeEdge,
    saveState,
    handleNodeCollapse,
    isValidConnection,
    addNode,
    isErpToEcomm,
    toggleDirection,
    hasUnsavedChanges,
    selectedEntity,
    isLoadingSchema,
  } = useGraphService();

  const reactFlowInstance = useReactFlow();

  // Component state
  const [isEdgeCreationMode, setIsEdgeCreationMode] = useState(false);
  const [edgeSource, setEdgeSource] = useState(null);
  const [showTransformConfig, setShowTransformConfig] = useState(false);
  const [selectedTransformType, setSelectedTransformType] = useState('none');
  const [transformOptions, setTransformOptions] = useState({});
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);

  // New state for GraphSearch highlighting
  const [highlightedNodes, setHighlightedNodes] = useState(new Set());
  const [highlightedEdges, setHighlightedEdges] = useState(new Set());
  const highlightTimerRef = useRef(null);

  // Debug state
  const [, setDebugInfo] = useState({
    selectedNodeId: null,
    selectedEdgeId: null,
    lastAction: 'none'
  });

  // Component state for delete confirmation popup
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [edgeToDelete, setEdgeToDelete] = useState(null);

  // Lock the direction toggle from click until the new direction's schema has loaded.
  // toggleDirection only starts loading ~100ms after the click and does not cancel earlier
  // requests, so rapid toggles overlap loads (stale data, ResizeObserver loop errors).
  const [isSwitchingDirection, setIsSwitchingDirection] = useState(false);
  const switchLoadStartedRef = useRef(false);

  const handleToggleDirection = useCallback(() => {
    if (isSwitchingDirection || isLoadingSchema) return;
    toggleDirection();
    // Virtual entities switch locally without a schema load, so there is nothing to wait for
    if (!selectedEntity?.isVirtual) {
      switchLoadStartedRef.current = false;
      setIsSwitchingDirection(true);
    }
  }, [isSwitchingDirection, isLoadingSchema, toggleDirection, selectedEntity]);

  useEffect(() => {
    if (!isSwitchingDirection) return;
    if (isLoadingSchema) {
      switchLoadStartedRef.current = true;
    } else if (switchLoadStartedRef.current) {
      setIsSwitchingDirection(false);
    }
  }, [isSwitchingDirection, isLoadingSchema]);

  const isDirectionLocked = isSwitchingDirection || isLoadingSchema;

  // React Flow ignores unmeasured nodes in fitView, so wait until every node has its size
  const nodesInitialized = useNodesInitialized();

  // Frame the ERP and eCommerce root nodes. The field columns can be thousands of pixels
  // tall, so fitting everything would zoom out too far to read anything.
  const fitToRootNodes = useCallback(() => {
    const rootNodes = reactFlowInstance.getNodes().filter((node) => node.data?.isRootNode && !node.hidden);
    reactFlowInstance.fitView({
      nodes: rootNodes.length ? rootNodes.map((node) => ({ id: node.id })) : undefined,
      padding: 0.04,
      maxZoom: 1.5
    });
  }, [reactFlowInstance]);

  // Show both root nodes once the graph (skeleton or loaded schema) has been measured.
  // nodesInitialized flips inside React Flow's ResizeObserver callback, so defer the fit to
  // the next frame (outside that callback) and frame each node set only once.
  const lastFittedCountRef = useRef(0);
  useEffect(() => {
    if (nodes.length === 0) {
      // Graph cleared (e.g. direction toggle): fit whatever loads next
      lastFittedCountRef.current = 0;
      return undefined;
    }
    if (!nodesInitialized || lastFittedCountRef.current === nodes.length) {
      return undefined;
    }
    const frame = requestAnimationFrame(() => {
      lastFittedCountRef.current = nodes.length;
      fitToRootNodes();
    });
    return () => cancelAnimationFrame(frame);
  }, [nodesInitialized, nodes.length, fitToRootNodes]);

  // Highlight animations (node-highlight, edge-highlight-path, node-connectable,
  // node-incompatible) live in GraphMapper.module.css under :global.

  // Clean up highlight timer on unmount
  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
      }
    };
  }, []);
  
  // Debug panel click events
  const debugClick = useCallback((event, element, type) => {
    // Update debug info
    setDebugInfo(prev => ({
      ...prev,
      lastAction: `${type} click`,
      [`selected${type.charAt(0).toUpperCase() + type.slice(1)}Id`]: element.id
    }));
    
    // Stop propagation to ensure the event isn't handled multiple times
    event.stopPropagation();
  }, []);
  
  // Handle node selection for manual edge creation
  const onNodeClick = useCallback((event, node) => {
    debugClick(event, node, 'node');
    
    if (isEdgeCreationMode) {
      // Only leaf nodes can participate in mapping
      if (node.data.hasChildren || node.data.isRootNode) {
        alert('Only field nodes can be connected. This node has children or is a root node.');
        return;
      }
      
      if (!edgeSource) {
        // We're selecting the first node
        setEdgeSource(node);
        selectNode(node.id);
        
        // Highlight compatible target nodes
        const targetNodes = nodes.filter(n => {
          // Only consider leaf nodes on the opposite side
          const isLeafNode = !n.data.hasChildren && !n.data.isRootNode;
          const isOppositeSide = (node.data.parentId === 'customer' && n.data.parentId === 'client') ||
                                 (node.data.parentId === 'client' && n.data.parentId === 'customer');
          
          if (!isLeafNode || !isOppositeSide) return false;
          
          // Check if connection would be valid
          const connection = node.data.parentId === 'customer' ?
            { source: node.id, target: n.id } :
            { source: n.id, target: node.id };
            
          return isValidConnection(connection);
        });
        
        // Highlight compatible nodes and dim incompatible ones
        const newHighlightedNodes = new Set([node.id, ...targetNodes.map(n => n.id)]);
        setHighlightedNodes(newHighlightedNodes);
        
        // Clear any existing highlight timer
        if (highlightTimerRef.current) {
          clearTimeout(highlightTimerRef.current);
        }
      } else {
        // We're selecting the second node
        if (node.id === edgeSource.id) {
          return;
        }
        
        // Validate the connection
        if (!isValidConnection({
          source: edgeSource.id,
          target: node.id
        })) {
          alert('This is not a valid connection. Make sure you are connecting leaf nodes from opposite sides.');
          return;
        }
        
        // Create the mapping edge
        const params = {
          source: edgeSource.id,
          target: node.id,
          sourceHandle: 'r',
          targetHandle: 'l',
          type: 'custom',
          data: {
            label: 'New Mapping',
            transformationType: 'none',
            hasTransformation: false
          }
        };
        
        handleConnect(params);
        
        // Reset selection
        selectNode(null);
        
        // Reset edge creation mode
        setIsEdgeCreationMode(false);
        setEdgeSource(null);
        
        // Clear highlights
        setHighlightedNodes(new Set());
      }
    } else {
      // Regular node selection
      setSelectedNode(node);
      setSelectedEdge(null);
      // Close transform config panel
      setShowTransformConfig(false);
    }
  }, [isEdgeCreationMode, edgeSource, selectNode, handleConnect, debugClick, isValidConnection, nodes, setHighlightedNodes, highlightTimerRef]);

  // Function to delete a connection (edge)
  const deleteConnection = useCallback((edgeId) => {
    // Remove the edge from the graph
    removeEdge(edgeId);

    // Clear the selected edge if it matches the deleted edge
    setSelectedEdge((prevEdge) => (prevEdge?.id === edgeId ? null : prevEdge));
  }, [removeEdge]);

  // Mapping edges on the selected node (tree edges are structure, not mappings). System roots are never deletable.
  const selectedNodeEdges = useMemo(() => {
    if (!selectedNode || selectedNode.id === 'customer' || selectedNode.id === 'client') return [];
    return edges.filter(edge =>
      edge.type !== 'tree' &&
      !edge.id.startsWith('tree-edge-') &&
      (edge.source === selectedNode.id || edge.target === selectedNode.id));
  }, [selectedNode, edges]);

  // Delete in Node Properties: removes every mapping edge on the node through removeEdge, exactly like
  // Remove in Edge Properties, so Payment / Shipping / Sales Person drop those pairs on the next Save.
  const handleDeleteNode = useCallback(() => {
    selectedNodeEdges.forEach(edge => deleteConnection(edge.id));
    setSelectedNode(null);
  }, [selectedNodeEdges, deleteConnection]);

  // Function to handle delete confirmation
  const confirmDeleteConnection = useCallback(() => {
    if (edgeToDelete) {
      deleteConnection(edgeToDelete.id);
      setEdgeToDelete(null);
      setShowDeletePopup(false);
    }
  }, [edgeToDelete, deleteConnection]);

  // Function to cancel delete
  const cancelDeleteConnection = useCallback(() => {
    setEdgeToDelete(null);
    setShowDeletePopup(false);
  }, []);

  // Handle edge selection
  const onEdgeClick = useCallback((event, edge) => {
    debugClick(event, edge, 'edge');
    
    if (!isEdgeCreationMode) {
      // Skip selection for tree edges
      if (edge.id.startsWith('tree-edge-')) {
        return;
      }
      
      setSelectedEdge(edge);
      setSelectedNode(null);
      
      // Load transformation data if available
      if (edge.data?.transformationType) {
        setSelectedTransformType(edge.data.transformationType);
        setTransformOptions(edge.data.options || {});
      } else {
        setSelectedTransformType('none');
        setTransformOptions({});
      }
      
      // Close transform config panel
      setShowTransformConfig(false);

      // Show delete confirmation popup
      setEdgeToDelete(edge);
      setShowDeletePopup(true);
    }
  }, [isEdgeCreationMode, debugClick]);

  // Toggle edge creation mode
  const handleToggleEdgeCreationMode = useCallback(() => {
    // If we're exiting edge creation mode, reset the selected source node
    if (isEdgeCreationMode && edgeSource) {
      selectNode(null);
    }
    
    setIsEdgeCreationMode(!isEdgeCreationMode);
    setEdgeSource(null);
    setSelectedNode(null);
    setSelectedEdge(null);
    setShowTransformConfig(false);
    
    // Clear highlights when toggling edge creation mode
    setHighlightedNodes(new Set());
    setHighlightedEdges(new Set());
  }, [isEdgeCreationMode, edgeSource, selectNode, setHighlightedNodes, setHighlightedEdges]);

  // Function to toggle transform configuration panel
  const toggleTransformConfig = useCallback(() => {
    setShowTransformConfig(!showTransformConfig);
  }, [showTransformConfig]);
  
  // Function to update transformation type
  const updateTransformationType = useCallback((edgeId, transformType) => {
    setSelectedTransformType(transformType);
    
    // Set default options based on transform type
    let defaultOptions = {};
    
    switch(transformType) {
      case 'formatCurrency':
        defaultOptions = { locale: 'en-US', style: 'currency', currency: 'USD' };
        break;
      case 'formatDate':
        defaultOptions = { format: 'MM/DD/YYYY' };
        break;
      case 'formatNumber':
        defaultOptions = { precision: 2, thousandSeparator: true };
        break;
      default:
        defaultOptions = {};
    }
    
    setTransformOptions(defaultOptions);
    setEdgeTransformation(edgeId, transformType, defaultOptions);
  }, [setEdgeTransformation]);
  
  // Function to update transformation options
  const updateTransformOptions = useCallback((edgeId, newOptions) => {
    setTransformOptions(prevOptions => ({
      ...prevOptions,
      ...newOptions
    }));
    
    // Get the current transform type
    const transformType = selectedTransformType;
    
    // Update the edge with new options
    setEdgeTransformation(edgeId, transformType, {
      ...transformOptions,
      ...newOptions
    });
  }, [selectedTransformType, transformOptions, setEdgeTransformation]);
  
  // Function to save transformation configuration
  const saveTransformConfiguration = useCallback(() => {
    if (!selectedEdge) return;
    
    // Close the panel
    setShowTransformConfig(false);
    
    // Persist the state
    saveState();
  }, [selectedEdge, saveState]);

  // Function to handle node collapse with visualization effect
  const handleNodeCollapseWithViz = useCallback((nodeId) => {
    handleNodeCollapse(nodeId);
    
    // After a slight delay to allow state updates, fit view
    setTimeout(() => {
      if (reactFlowInstance) {
        reactFlowInstance.fitView({ padding: 0.04 });
      }
    }, 100);
  }, [handleNodeCollapse, reactFlowInstance]);

  // Handle background/pane click to clear selection
  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    setSelectedEdge(null);
    setShowTransformConfig(false);
    
    // Clear highlights when clicking on the pane
    setHighlightedNodes(new Set());
    setHighlightedEdges(new Set());
    
    if (isEdgeCreationMode) {
      if (edgeSource) {
        selectNode(null);
      }
      setIsEdgeCreationMode(false);
      setEdgeSource(null);
    }
  }, [isEdgeCreationMode, edgeSource, selectNode, setHighlightedNodes, setHighlightedEdges]);
  
  // Restored edge highlighting with a clean, professional appearance
  const handleNodeHighlight = useCallback((nodeId) => {
    // Start with a clean slate
    const nodesToHighlight = new Set([nodeId]);
    const edgesToHighlight = new Set();
    
    // Find the node
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;
    
    // Add the parent node to highlight if it exists
    if (node.data?.parentId) {
      nodesToHighlight.add(node.data.parentId);
      
      // Find and highlight the tree edge to the parent
      const parentEdgeId = `tree-edge-${node.data.parentId}-${nodeId}`;
      const parentEdge = edges.find(e => e.id === parentEdgeId);
      if (parentEdge) {
        edgesToHighlight.add(parentEdge.id);
      }
    }
    
    // Find direct connection edges (field mapping edges only)
    const directEdges = edges.filter(
      edge => (edge.source === nodeId || edge.target === nodeId) && 
             !edge.id.startsWith('tree-edge-')
    );
    
    // Add these edges to highlight
    directEdges.forEach(edge => {
      edgesToHighlight.add(edge.id);
    });
    
    // Update state with our highlight sets
    setHighlightedNodes(nodesToHighlight);
    setHighlightedEdges(edgesToHighlight);
    
    // After 8 seconds, clear highlights
    if (highlightTimerRef.current) {
      clearTimeout(highlightTimerRef.current);
    }
    
    highlightTimerRef.current = setTimeout(() => {
      setHighlightedNodes(new Set());
      setHighlightedEdges(new Set());
    }, 8000);
    
    // Center the view on the GraphSearched node
    if (reactFlowInstance) {
      const nodeToCenter = nodes.find(n => n.id === nodeId);
      if (nodeToCenter) {
        reactFlowInstance.setCenter(
          nodeToCenter.position.x + 100, 
          nodeToCenter.position.y, 
          { duration: 800, zoom: 1.2 }
        );
      }
    }
  }, [edges, nodes, reactFlowInstance]);
  
  // Find all parent nodes up to root
  const getNodeParentChain = useCallback((nodeId) => {
    const parentChain = [];
    let currentNodeId = nodeId;
    
    // Traverse up the parent chain
    while (currentNodeId) {
      const lookupId = currentNodeId;
      const node = nodes.find(n => n.id === lookupId);
      if (!node) break;
      
      parentChain.push(node);
      currentNodeId = node.data.parentId;
    }
    
    return parentChain;
  }, [nodes]);
  
  // Expand all parent nodes to reveal a specific node
  const handleExpandToNode = useCallback((nodeId) => {
    
    // Get parent chain for this node
    const parentChain = getNodeParentChain(nodeId);
    
    // Expand each collapsed parent (from root to leaf)
    // Start from the end (root) and work down to the node
    for (let i = parentChain.length - 1; i >= 0; i--) {
      const node = parentChain[i];
      
      // If this parent is collapsed, expand it
      if (node.data.isCollapsed) {
        handleNodeCollapse(node.id);
      }
    }
    
    // Center the view on the node after a delay to allow for the expansion
    setTimeout(() => {
      const nodeToCenter = nodes.find(n => n.id === nodeId);
      if (nodeToCenter && reactFlowInstance) {
        reactFlowInstance.setCenter(
          nodeToCenter.position.x + 100, 
          nodeToCenter.position.y, 
          { duration: 800, zoom: 1.2 }
        );
      }
    }, 300);
  }, [nodes, handleNodeCollapse, getNodeParentChain, reactFlowInstance]);

  const handleAddCustomerNode = useCallback(() => {
    const newNodeLabel = prompt('Enter label for the new customer node:');
    if (newNodeLabel && newNodeLabel.trim() !== '') {
      const newNodeId = addNode('left', 'field', newNodeLabel); // Add to the left side under customer
      setTimeout(() => {
        if (reactFlowInstance) {
          reactFlowInstance.fitView({ padding: 0.04 });
          handleNodeHighlight(newNodeId); // Highlight the new node
        }
      }, 100);
    }
  }, [addNode, reactFlowInstance, handleNodeHighlight]);

  const nodesWithHighlight = useMemo(() => {
    return nodes.map(node => {
      if (node.id === 'customer') {
        // Add a "+" button inside the customer node
        return {
          ...node,
          data: {
            ...node.data,
            customContent: (
              <button
                type="button"
                className="btn btn-success btn-sm shadow-sm"
                onClick={handleAddCustomerNode}
                title="Add Customer Node"
              >
                +
              </button>
            )
          }
        };
      }
      
      // Apply highlighting based on connection state
      if (isEdgeCreationMode && edgeSource) {
        // Check if this is the source node
        if (node.id === edgeSource.id) {
          return {
            ...node,
            className: `${node.className || ''} node-highlight`
          };
        }
        
        // Only apply styles to leaf nodes
        if (!node.data.hasChildren && !node.data.isRootNode) {
          // Check if it's on the opposite side
          const isOppositeSide = (edgeSource.data.parentId === 'customer' && node.data.parentId === 'client') ||
                                   (edgeSource.data.parentId === 'client' && node.data.parentId === 'customer');
          
          if (isOppositeSide) {
            // Check if connection would be valid
            const connection = edgeSource.data.parentId === 'customer' ?
              { source: edgeSource.id, target: node.id } :
              { source: node.id, target: edgeSource.id };
              
            const isValid = isValidConnection(connection);
            
            if (isValid) {
              return {
                ...node,
                className: `${node.className || ''} node-connectable`
              };
            } else {
              return {
                ...node,
                className: `${node.className || ''} node-incompatible`
              };
            }
          }
        }
      }
      
      // Apply general highlighting (for search, etc.)
      if (highlightedNodes.has(node.id)) {
        return {
          ...node,
          className: `${node.className || ''} node-highlight`
        };
      }
      
      return node;
    });
  }, [nodes, highlightedNodes, handleAddCustomerNode, isEdgeCreationMode, edgeSource, isValidConnection]);

  // Create a modified edges array with highlight classes (for path only)
  const edgesWithHighlight = useMemo(() => {
    return edges.map(edge => {
      if (highlightedEdges.has(edge.id)) {
        // Clone the edge and add highlight styling for path only
        return {
          ...edge,
          // Add a CSS class that targets only the edge path
          className: `${edge.className || ''} edge-highlight-path`,
          // No other style changes that would affect labels or markers
          zIndex: 10, // Bring highlighted edges slightly forward
          // Remove any existing animated prop to prevent conflicts with our animation
          animated: false
        };
      }
      return edge;
    });
  }, [edges, highlightedEdges]);


  const toolbar = (
    <div className={Style.toolbar}>
      {canEdit && <button
        type="button"
        className={`${Style.toolButton} ${isEdgeCreationMode ? Style.toolButtonActive : ''}`}
        onClick={handleToggleEdgeCreationMode}
        title="Create mapping edge"
      >
        <i className="uil-link-add" aria-hidden="true" />
      </button>}
      {canEdit && <button
        type="button"
        className={`${Style.toolButton} ${(hasUnsavedChanges || selectedEntity?.isVirtual) ? '' : Style.toolButtonDisabled}`}
        onClick={saveState}
        disabled={!hasUnsavedChanges && !selectedEntity?.isVirtual}
        title={(hasUnsavedChanges || selectedEntity?.isVirtual) ? "Save" : "No unsaved changes"}
      >
        <i className="uil-save" aria-hidden="true" />
      </button>}
      <button
        type="button"
        className={Style.toolButton}
        onClick={() => reactFlowInstance.fitView({ padding: 0.04 })}
        title="Fit View"
      >
        <i className="uil-expand-arrows-alt" aria-hidden="true" />
      </button>
    </div>
  );

  return (
    <div className={Style.root}>
      {/* Toolbar, direction toggle and search sit in the mapping screen header */}
      {toolbarSlot && createPortal(
        <div className={Style.headerBar}>
          {toolbar}

          {/* Direction Toggle + Custom Attributes Bar */}
          <div className={Style.topBar}>
            {/* Direction Toggle Card */}
            <div className={Style.barCard}>
              <div className="d-flex align-items-center gap-3">
                <span className={Style.directionLabel}>Direction:</span>
                <button
                  type="button"
                  onClick={handleToggleDirection}
                  disabled={isDirectionLocked}
                  title={isDirectionLocked ? 'Loading mappings…' : 'Switch mapping direction'}
                  className={`${Style.switch} ${isErpToEcomm ? Style.switchOn : ''}`}
                  aria-pressed={isErpToEcomm}
                  aria-label="Toggle mapping direction"
                >
                  <span className={Style.switchThumb} />
                </button>
                <span className={Style.directionValue}>
                  {isErpToEcomm ? 'ERP → eCommerce' : 'eCommerce → ERP'}
                </span>
              </div>
            </div>

            {/* Custom Attributes panel — shown only for virtual entities */}
            {canEdit && selectedEntity?.isVirtual && (() => {
              const entityType = selectedEntity?.entityName?.toLowerCase();
              const type = entityType === 'shipping' ? 'shipping'
                : entityType === 'payment' ? 'payment'
                : entityType === 'sales person' ? 'salesperson'
                : 'custom';
              return (
                <div className={Style.barCard}>
                  <CustomAttributesPanel type={type} />
                </div>
              );
            })()}
          </div>

          {/* GraphSearch Component */}
          <GraphSearch
            nodes={nodes}
            edges={edges}
            onNodeHighlight={handleNodeHighlight}
            onExpandToNode={handleExpandToNode}
          />
        </div>,
        toolbarSlot
      )}

      <div className={Style.canvas}>
        {/* Edge creation mode indicator */}
        {isEdgeCreationMode && (
          <div className={Style.modeHint}>
            <p className={Style.modeHintTitle}>
              {!edgeSource
                ? 'Select a field node from either side'
                : 'Now select a field node from the opposite side'}
            </p>
            <p className={Style.modeHintText}>
              Only leaf/field nodes can be connected for mapping
            </p>
          </div>
        )}

        {/* ReactFlow Canvas */}
        <ReactFlow
          nodes={nodesWithHighlight}
          edges={edgesWithHighlight}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={canEdit ? handleConnect : undefined}
          nodesConnectable={Boolean(canEdit)}
          deleteKeyCode={canEdit ? 'Backspace' : null}
          isValidConnection={isValidConnection}
          onNodeClick={onNodeClick}
          onEdgeClick={onEdgeClick}
          onPaneClick={onPaneClick}
          fitView
          fitViewOptions={{ padding: 0.04 }}
          attributionPosition="hidden"
          minZoom={0.1}
          maxZoom={1.5}
          nodesDraggable={true}
          elementsSelectable={true}
          zoomOnScroll={true}
          defaultEdgeOptions={{
            type: 'custom',
            style: { stroke: EDGE_COLOR, strokeWidth: 2 },
            animated: true,
            markerEnd: {
              type: MarkerType.ArrowClosed,
            },
          }}
        >
          <Controls />
          <Background pattern="dots" gap={12} size={1} />

          {/* Node property panel */}
          {selectedNode && (
            <Panel position="top-right" className={Style.sidePanel}>
              <NodePanel
                node={selectedNode}
                onClose={() => setSelectedNode(null)}
                onCollapse={handleNodeCollapseWithViz}
                onDelete={handleDeleteNode}
                canDelete={selectedNodeEdges.length > 0}
                canEdit={canEdit}
              />
            </Panel>
          )}

          {/* Edge property panel */}
          {selectedEdge && !showTransformConfig && (
            <Panel position="top-right" className={Style.sidePanel}>
              <EdgePanel
                edge={selectedEdge}
                nodes={nodes}
                transformationTypes={transformationTypes}
                selectedTransformType={selectedTransformType}
                onClose={() => setSelectedEdge(null)}
                onUpdateLabel={(label) => updateEdgeLabel(selectedEdge.id, label)}
                onUpdateTransformationType={(type) => updateTransformationType(selectedEdge.id, type)}
                onConfigureTransform={toggleTransformConfig}
                onRemoveEdge={() => removeEdge(selectedEdge.id)}
                canEdit={canEdit}
              />
            </Panel>
          )}

          {/* Transformation configuration panel */}
          {selectedEdge && showTransformConfig && (
            <Panel position="top-right" className={Style.sidePanel}>
              <TransformPanel
                edge={selectedEdge}
                transformationType={selectedTransformType}
                transformOptions={transformOptions}
                onUpdateOptions={(options) => updateTransformOptions(selectedEdge.id, options)}
                onSave={saveTransformConfiguration}
                onCancel={() => setShowTransformConfig(false)}
              />
            </Panel>
          )}
        </ReactFlow>
      </div>

      {/* Delete Confirmation Popup - HIDDEN (kept disabled, as in the source app) */}
      {false && showDeletePopup && (
        <Modal show onHide={cancelDeleteConnection} centered>
          <Modal.Header closeButton>
            <Modal.Title className={Style.modalTitle}>Delete Connection</Modal.Title>
          </Modal.Header>
          <Modal.Body className={Style.modalBody}>
            Are you sure you want to delete the connection: <strong>{edgeToDelete?.data?.label || edgeToDelete?.id}</strong>?
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={cancelDeleteConnection}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={confirmDeleteConnection}
            >
              Delete
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default GraphMapper;
